import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import { CalendarOutlined, CaretDownOutlined, CloseOutlined } from '@ant-design/icons'
import moment, { Moment } from 'moment'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DateRangeValue = [Moment, Moment]

export type DateRangePreset =
	| 'custom'
	| 'today'
	| 'yesterday'
	| 'this_week'
	| 'last_7'
	| 'last_week'
	| 'last_28'
	| 'last_30'
	| 'this_month'
	| 'last_month'

interface DateRangePresetPickerProps {
	value: DateRangeValue
	onChange: (value: DateRangeValue, preset: DateRangePreset) => void
	/** When provided, shows an X in the trigger pill to clear the filter */
	onClear?: () => void
}

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

const PRESETS: { key: DateRangePreset; label: string }[] = [
	{ key: 'custom', label: 'Vlastné' },
	{ key: 'today', label: 'Dnes' },
	{ key: 'yesterday', label: 'Včera' },
	{ key: 'this_week', label: 'Tento týždeň' },
	{ key: 'last_7', label: 'Posledných 7 dní' },
	{ key: 'last_week', label: 'Minulý týždeň' },
	{ key: 'last_28', label: 'Posledných 28 dní' },
	{ key: 'last_30', label: 'Posledných 30 dní' },
	{ key: 'this_month', label: 'Tento mesiac' },
	{ key: 'last_month', label: 'Minulý mesiac' }
]

function resolvePreset(key: DateRangePreset): DateRangeValue | null {
	const now = moment()
	if (key === 'today') return [now.clone().startOf('day'), now.clone().endOf('day')]
	if (key === 'yesterday') {
		return [
			now.clone().subtract(1, 'day').startOf('day'),
			now.clone().subtract(1, 'day').endOf('day')
		]
	}
	if (key === 'this_week') return [now.clone().startOf('isoWeek'), now.clone().endOf('isoWeek')]
	if (key === 'last_7') return [now.clone().subtract(6, 'days').startOf('day'), now.clone().endOf('day')]
	if (key === 'last_week') {
		return [
			now.clone().subtract(1, 'week').startOf('isoWeek'),
			now.clone().subtract(1, 'week').endOf('isoWeek')
		]
	}
	if (key === 'last_28') return [now.clone().subtract(27, 'days').startOf('day'), now.clone().endOf('day')]
	if (key === 'last_30') return [now.clone().subtract(29, 'days').startOf('day'), now.clone().endOf('day')]
	if (key === 'this_month') return [now.clone().startOf('month'), now.clone().endOf('month')]
	if (key === 'last_month') {
		return [
			now.clone().subtract(1, 'month').startOf('month'),
			now.clone().subtract(1, 'month').endOf('month')
		]
	}
	return null
}

function detectPreset(range: DateRangeValue): DateRangePreset {
	const match = PRESETS.find((p) => {
		if (p.key === 'custom') return false
		const resolved = resolvePreset(p.key)
		return (
			resolved !== null
			&& range[0].isSame(resolved[0], 'minute')
			&& range[1].isSame(resolved[1], 'minute')
		)
	})
	return match ? match.key : 'custom'
}

function formatShortDate(m: Moment): string {
	return m.format('D. M. YYYY')
}

// ---------------------------------------------------------------------------
// MonthGrid — single calendar month
// ---------------------------------------------------------------------------

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const DAY_NAMES = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']

interface MonthGridProps {
	month: Moment
	onPrev: () => void
	onNext: () => void
	showPrev: boolean
	showNext: boolean
	onDayClick: (day: Moment) => void
	inRange: (day: Moment) => boolean
	isStart: (day: Moment) => boolean
	isEnd: (day: Moment) => boolean
	isToday: (day: Moment) => boolean
}

const MonthGrid: React.FC<MonthGridProps> = ({
	month, onPrev, onNext, showPrev, showNext,
	onDayClick, inRange, isStart, isEnd, isToday
}) => {
	// Build 42-cell grid starting from Monday of the week containing the 1st
	const startOfMonth = month.clone().startOf('month')
	const gridStart = startOfMonth.clone().startOf('isoWeek')

	// Use Array.from with index offset — avoids any moment mutation pitfalls
	const cells: Moment[] = Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, 'days'))

	// Trim trailing rows that are fully outside this month
	while (cells.length > 7 && cells[cells.length - 7].month() !== month.month()) {
		cells.splice(cells.length - 7, 7)
	}

	return (
		<div style={{ width: 224 }}>
			{/* Header */}
			<div style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				marginBottom: 8
			}}>
				{showPrev
					? (
						<button
							type="button"
							onClick={onPrev}
							style={{
								border: 'none',
								background: 'none',
								cursor: 'pointer',
								fontSize: 18,
								color: '#6b7280',
								padding: '0 4px',
								lineHeight: 1
							}}
						>
							&#8249;
						</button>
					)
					: <span style={{ width: 26 }} />}
				<span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
					{MONTH_NAMES[month.month()]} {month.year()}
				</span>
				{showNext
					? (
						<button
							type="button"
							onClick={onNext}
							style={{
								border: 'none',
								background: 'none',
								cursor: 'pointer',
								fontSize: 18,
								color: '#6b7280',
								padding: '0 4px',
								lineHeight: 1
							}}
						>
							&#8250;
						</button>
					)
					: <span style={{ width: 26 }} />}
			</div>

			{/* Day name headers */}
			<div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
				{DAY_NAMES.map((d) => (
					<div
						key={d}
						style={{
							textAlign: 'center',
							fontSize: 11,
							color: '#9ca3af',
							fontWeight: 500,
							paddingBottom: 4
						}}
					>
						{d}
					</div>
				))}
			</div>

			{/* Day cells */}
			<div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
				{cells.map((day, idx) => {
					const inCurrentMonth = day.month() === month.month()
					const start = isStart(day)
					const end = isEnd(day)
					const highlighted = inRange(day)
					const today = isToday(day)
					const circled = start || end

					const bgRow = highlighted && !circled ? '#dbeafe' : 'transparent'
					let cellRadius: string | number = 0
					if (start) cellRadius = '50% 0 0 50%'
					else if (end) cellRadius = '0 50% 50% 0'

					let textColor = '#d1d5db'
					if (circled) textColor = '#fff'
					else if (inCurrentMonth) textColor = '#111827'

					return (
						<div
							key={idx} // eslint-disable-line react/no-array-index-key
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 30,
								background: bgRow,
								borderRadius: cellRadius
							}}
						>
							<button
								type="button"
								onClick={() => { if (inCurrentMonth) onDayClick(day) }}
								style={{
									width: 28,
									height: 28,
									borderRadius: '50%',
									border: today && !circled ? '1.5px solid #1d4ed8' : 'none',
									background: circled ? '#1d4ed8' : 'transparent',
									color: textColor,
									fontSize: 12,
									cursor: inCurrentMonth ? 'pointer' : 'default',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontWeight: circled || today ? 600 : 400,
									flexShrink: 0
								}}
							>
								{day.date()}
							</button>
						</div>
					)
				})}
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------
// InlineRangeCalendar — two-month view with range selection
// ---------------------------------------------------------------------------

interface InlineRangeCalendarProps {
	value: DateRangeValue
	onChange: (range: DateRangeValue) => void
}

const InlineRangeCalendar: React.FC<InlineRangeCalendarProps> = ({ value, onChange }) => {
	const [leftMonth, setLeftMonth] = useState<Moment>(() => value[0].clone().startOf('month'))
	const [selectingStart, setSelectingStart] = useState<Moment | null>(null)

	const rightMonth = leftMonth.clone().add(1, 'month')

	const handleDayClick = (day: Moment) => {
		if (!selectingStart) {
			setSelectingStart(day.clone().startOf('day'))
		} else {
			const isBefore = selectingStart.isSameOrBefore(day, 'day')
			const start = isBefore ? selectingStart.clone() : day.clone().startOf('day')
			const end = isBefore ? day.clone().endOf('day') : selectingStart.clone().endOf('day')
			onChange([start, end])
			setSelectingStart(null)
		}
	}

	const inRange = (day: Moment): boolean => {
		if (selectingStart) {
			const lo = selectingStart.isSameOrBefore(day, 'day') ? selectingStart : day
			const hi = selectingStart.isSameOrBefore(day, 'day') ? day : selectingStart
			return day.isBetween(lo, hi, 'day', '[]')
		}
		return day.isBetween(value[0], value[1], 'day', '[]')
	}

	const isStart = (day: Moment): boolean => {
		if (selectingStart) return day.isSame(selectingStart, 'day')
		return day.isSame(value[0], 'day')
	}

	const isEnd = (day: Moment): boolean => {
		if (selectingStart) return false
		return day.isSame(value[1], 'day')
	}

	const isToday = (day: Moment): boolean => day.isSame(moment(), 'day')

	return (
		<div style={{ display: 'flex', gap: 28 }}>
			<MonthGrid
				month={leftMonth}
				onPrev={() => setLeftMonth((m) => m.clone().subtract(1, 'month'))}
				onNext={() => setLeftMonth((m) => m.clone().add(1, 'month'))}
				showPrev
				showNext={false}
				onDayClick={handleDayClick}
				inRange={inRange}
				isStart={isStart}
				isEnd={isEnd}
				isToday={isToday}
			/>
			<MonthGrid
				month={rightMonth}
				onPrev={() => setLeftMonth((m) => m.clone().subtract(1, 'month'))}
				onNext={() => setLeftMonth((m) => m.clone().add(1, 'month'))}
				showPrev={false}
				showNext
				onDayClick={handleDayClick}
				inRange={inRange}
				isStart={isStart}
				isEnd={isEnd}
				isToday={isToday}
			/>
		</div>
	)
}

// ---------------------------------------------------------------------------
// DateRangePresetPicker — exported component
// ---------------------------------------------------------------------------

// Portal wrapper so the dropdown renders above the Drawer's overflow
function FixedPortal({ children, anchorRef, open }: {
	children: React.ReactNode
	anchorRef: React.RefObject<HTMLButtonElement>
	open: boolean
}) {
	const [style, setStyle] = useState<React.CSSProperties>({})

	useEffect(() => {
		if (!open || !anchorRef.current) return
		const rect = anchorRef.current.getBoundingClientRect()
		setStyle({
			position: 'fixed',
			top: rect.bottom + 6,
			right: window.innerWidth - rect.right,
			zIndex: 9999
		})
	}, [open, anchorRef])

	if (!open) return null

	return (
		<div style={style}>
			{children}
		</div>
	)
}

const DateRangePresetPicker: React.FC<DateRangePresetPickerProps> = ({ value, onChange, onClear }) => {
	const [open, setOpen] = useState(false)
	const [draftRange, setDraftRange] = useState<DateRangeValue>(value)
	const [draftPreset, setDraftPreset] = useState<DateRangePreset>(detectPreset(value))
	const triggerRef = useRef<HTMLButtonElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)

	// sync draft when popover opens
	useEffect(() => {
		if (open) {
			setDraftRange(value)
			setDraftPreset(detectPreset(value))
		}
	}, [open]) // eslint-disable-line react-hooks/exhaustive-deps

	// close on outside click
	useEffect(() => {
		if (!open) return undefined
		const handler = (e: MouseEvent) => {
			const target = e.target as Node
			const clickedTrigger = triggerRef.current && triggerRef.current.contains(target)
			const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(target)
			if (!clickedTrigger && !clickedDropdown) setOpen(false)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [open])

	const currentPreset = detectPreset(value)
	const presetLabel = PRESETS.find((p) => p.key === currentPreset)?.label ?? 'Vlastné'

	const handlePresetClick = (key: DateRangePreset) => {
		if (key === 'custom') {
			setDraftPreset('custom')
			return
		}
		const resolved = resolvePreset(key)
		if (resolved) {
			setDraftRange(resolved)
			setDraftPreset(key)
		}
	}

	const handleApply = () => {
		onChange(draftRange, draftPreset)
		setOpen(false)
	}

	const handleCancel = () => {
		setOpen(false)
	}

	return (
		<>
			{/* Trigger pill */}
			<button
				ref={triggerRef}
				type="button"
				onClick={() => setOpen((v) => !v)}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 6,
					padding: '5px 12px',
					border: '1px solid #d1d5db',
					borderRadius: 6,
					background: '#fff',
					cursor: 'pointer',
					fontSize: 13,
					color: '#374151',
					whiteSpace: 'nowrap'
				}}
			>
				<CalendarOutlined style={{ color: '#6b7280', fontSize: 14 }} />
				<span style={{ fontWeight: 600, color: '#1d4ed8' }}>{presetLabel}</span>
				<span style={{ color: '#6b7280' }}>
					{formatShortDate(value[0])}
					{' – '}
					{formatShortDate(value[1])}
				</span>
				{onClear
					? (
						<CloseOutlined
							style={{ color: '#1d4ed8', fontSize: 10, marginLeft: 2 }}
							onClick={(e) => { e.stopPropagation(); onClear() }}
						/>
					)
					: <CaretDownOutlined style={{ color: '#6b7280', fontSize: 11, marginLeft: 2 }} />}
			</button>

			{/* Dropdown rendered via fixed positioning — escapes Drawer overflow */}
			<FixedPortal anchorRef={triggerRef} open={open}>
				<div
					ref={dropdownRef}
					style={{
						background: '#fff',
						border: '1px solid #e5e7eb',
						borderRadius: 8,
						boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						minWidth: 560
					}}
				>
					<div style={{ display: 'flex' }}>
						{/* Left — preset list */}
						<div
							style={{
								width: 190,
								borderRight: '1px solid #f0f0f0',
								padding: '8px 0',
								flexShrink: 0
							}}
						>
							{PRESETS.map((p) => (
								<button
									key={p.key}
									type="button"
									onClick={() => handlePresetClick(p.key)}
									style={{
										display: 'block',
										width: '100%',
										padding: '7px 16px',
										textAlign: 'left',
										background: draftPreset === p.key ? '#eff6ff' : 'transparent',
										color: draftPreset === p.key ? '#1d4ed8' : '#374151',
										fontWeight: draftPreset === p.key ? 600 : 400,
										fontSize: 13,
										border: 'none',
										cursor: 'pointer'
									}}
								>
									{p.label}
								</button>
							))}
						</div>

						{/* Right — inline calendar */}
						<div style={{ padding: '16px 16px 8px 16px', flex: 1 }}>
							<InlineRangeCalendar
								value={draftRange}
								onChange={(range) => {
									setDraftRange(range)
									setDraftPreset('custom')
								}}
							/>
						</div>
					</div>

					{/* Footer */}
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: 8,
							padding: '10px 16px',
							borderTop: '1px solid #f0f0f0'
						}}
					>
						<Button size="small" onClick={handleCancel}>Zrušiť</Button>
						<Button size="small" type="primary" onClick={handleApply}>Použiť</Button>
					</div>
				</div>
			</FixedPortal>
		</>
	)
}

export default DateRangePresetPicker
